<template>
  <div
    :class="['select-list', 'pa-items', `select-${currentVariant}`]"
    :style="{ gap: computedGap, gridTemplateColumns: computedColumns }"
    :role="listRole"
  >
    <!-- The item is an option, so `radio` (or `checkbox` in multi-select) +
         `aria-checked` rather than a bare `button` (which announced no selected
         state). The image_card variant delegates both the role and the focus to
         the ImageButton it renders, so it stays unroled and unfocusable — and
         leaves the click to it too, or the bubbling click selects a second time.
         `data-part` names the option to analytics only: a `data-id` would make
         the editor and the agent treat it as a node of its own. -->
    <div
      v-for="(option, index) in fields.options?.options ?? []"
      :key="option.id"
      :data-part="option.id"
      :class="['list-item', 'pa-item', `variant-${currentVariant}`, { active: isSelected(option) }]"
      :style="{ '--pa-i': index }"
      :tabindex="isItemFocusable ? 0 : -1"
      :role="isItemFocusable ? itemRole : undefined"
      :aria-checked="isItemFocusable ? isSelected(option) : undefined"
      @click="isItemFocusable && handleSelect(option)"
      @keydown="onItemKeydown($event, option)"
    >
      <template v-if="currentVariant === 'text'">
        <div class="text-content" :class="{ 'has-checkbox': showCheck }">
          <span
            v-if="showCheck"
            :class="['option-checkbox', { checked: isSelected(option) }]"
            aria-hidden="true"
          >
            <svg viewBox="0 0 24 24" fill="none"><path :d="CHECK_PATH" /></svg>
          </span>
          <TipTap
            :model-value="pipe.getTranslation(option.label.key)"
            @update:model-value="(val) => pipe.setTranslation(option.label.key, val as object)"
            mode="single"
          />
        </div>
      </template>

      <template v-else-if="currentVariant === 'image_card'">
        <ImageButton
          :fields="{
            image: asImageField(option.image),
            label: option.label,
            imageObjectFit: fields.imageObjectFit,
            imageHeight: fields.imageHeight,
            imageFullBleed: fields.imageFullBleed,
            disableImageZoom: fields.disableImageZoom,
            imageBgColor: option.imageBackgroundColor ?? fields.imageBackgroundColor,
            bgColor: fields.bgColor,
            bgColorActive: fields.bgColorActive,
            labelColor: fields.textColor,
            labelFontSize: fields.textFontSize,
            labelLineHeight: fields.textLineHeight,
            labelFontWeight: fields.textFontWeight,
            borderColor: fields.borderColor,
            borderColorHover: fields.borderColorHover,
            borderRadius: fields.borderRadius,
            borderWidth: fields.borderWidth,
          }"
          v-bind="isMultiSelect ? { role: 'checkbox', 'aria-checked': isSelected(option) } : {}"
          @on-click="handleSelect(option)"
          :style="{ height: '100%' }"
        >
          <template v-if="showCheck" #label-start>
            <span :class="['option-checkbox', { checked: isSelected(option) }]" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none"><path :d="CHECK_PATH" /></svg>
            </span>
          </template>
        </ImageButton>
      </template>

      <template v-else>
        <div
          class="text-content"
          :class="{
            'order-2': currentVariant === 'image_inline_left',
            'has-checkbox': showCheck,
          }"
        >
          <span
            v-if="showCheck"
            :class="['option-checkbox', { checked: isSelected(option) }]"
            aria-hidden="true"
          >
            <svg viewBox="0 0 24 24" fill="none"><path :d="CHECK_PATH" /></svg>
          </span>
          <TipTap
            :model-value="pipe.getTranslation(option.label.key)"
            @update:model-value="(val) => pipe.setTranslation(option.label.key, val as object)"
            mode="single"
          />
        </div>
        <div
          class="image-wrapper inline"
          :class="{ 'order-1': currentVariant === 'image_inline_left' }"
          :style="{ backgroundColor: imageBgFor(option) }"
        >
          <PlainImage
            v-if="option.image?.id"
            :image="asImageField(option.image)!"
            :skeleton-color="imageSkeletonBgColor"
            :shimmer-color="imageSkeletonShimmerColor"
            class="inline-img"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { usePipe } from '@/render/composables/usePipe'
import { getColorStyleData, getSizeStyleData } from '@/render/composables/useStyleData'
import TipTap from '@render/lib/TipTap'
import type { ImageFieldType } from '@api/types.gen'
import type { ImageFieldData } from '@render/composables/useImagePreset'
import type { ColorField, SizeField } from '@/render/types/styles'
import type { SelectListOption } from '@render/types/fieldTypes'
import ImageButton from '../ImageButton/ImageButton.vue'
import PlainImage from '../../generic/PlainImage.vue'
import type { SelectionListFields } from './definition'
import { nextSelectedValues } from './multiSelection'

const pipe = usePipe()
const emit = defineEmits<{ (e: 'onClick', payload: string): void }>()

// An option's `image` is the generated `ImageFieldType`, whose `id` is optional;
// the render image pipeline takes `ImageFieldData`, which requires one. Narrow
// explicitly (dropping id-less values) rather than loosening `ImageFieldData`.
const asImageField = (image?: ImageFieldType): ImageFieldData | undefined =>
  image?.id ? (image as ImageFieldData) : undefined

const fields = defineModel<SelectionListFields>('fields', {
  required: true,
  default: () => ({
    variant: 'text',
    columns: undefined,
    gap: undefined,
    itemHeight: undefined,
    options: { options: [] },
    showCheck: false,
  }),
})

function color(f: ColorField | undefined, fallback: string) {
  return getColorStyleData(f) || fallback
}
function size(f: SizeField | undefined, fallback: string) {
  return getSizeStyleData(f) || fallback
}

// Per-option override falls back to the component-wide default, then transparent
// (unset — matches the pre-existing hardcoded-transparent inline background).
function imageBgFor(option: SelectListOption) {
  return (
    getColorStyleData(option.imageBackgroundColor ?? fields.value.imageBackgroundColor) ??
    'transparent'
  )
}

const imageSkeletonBgColor = computed(
  () => getColorStyleData(fields.value.imageSkeletonBgColor) ?? 'transparent',
)
const imageSkeletonShimmerColor = computed(
  () => getColorStyleData(fields.value.imageSkeletonShimmerColor) ?? 'transparent',
)

const currentVariant = computed(() => fields.value.variant || 'text')

const isMultiSelect = computed(() => fields.value.multiSelect === true)
const showCheck = computed(() => isMultiSelect.value && fields.value.showCheck === true)

// Every variant except image_card renders its own interactive item; image_card
// nests an ImageButton that already owns role="button" + focus (re-roled as the
// checkbox in multi-select).
const isItemFocusable = computed(() => currentVariant.value !== 'image_card')
const itemRole = computed(() => (isMultiSelect.value ? 'checkbox' : 'radio'))
const listRole = computed(() => {
  if (isMultiSelect.value) return 'group'
  return isItemFocusable.value ? 'radiogroup' : undefined
})

const CHECK_PATH = 'M6 12.3636L9.75 16L18 8'

const computedColumns = computed(() => {
  const cols = fields.value.columns || (currentVariant.value === 'image_card' ? 2 : 1)
  return `repeat(${cols}, 1fr)`
})

const computedGap = computed(() => {
  return (fields.value.gap ? getSizeStyleData(fields.value.gap) : 'var(--space-16)') ?? undefined
})

const computedItemHeight = computed(() => {
  return fields.value.itemHeight ? getSizeStyleData(fields.value.itemHeight) : undefined
})

const css = computed(() => ({
  bg: color(fields.value.bgColor, 'var(--color-action-tertiary-background)'),
  bgActive: color(fields.value.bgColorActive, 'var(--color-action-tertiary-background-pressed)'),
  textColor: color(fields.value.textColor, 'var(--color-action-tertiary-text)'),
  textFontSize: size(fields.value.textFontSize, 'var(--text-label-md-size)'),
  textLineHeight: size(fields.value.textLineHeight, 'var(--text-label-md-line-height)'),
  textFontWeight: fields.value.textFontWeight || 'var(--text-label-md-weight)',
  borderColor: color(fields.value.borderColor, 'var(--color-action-tertiary-border)'),
  borderColorHover: color(
    fields.value.borderColorHover,
    'var(--color-action-tertiary-border-pressed)',
  ),
  borderColorActive: color(
    fields.value.borderColorActive,
    'var(--color-action-tertiary-border-pressed)',
  ),
  borderRadius: size(fields.value.borderRadius, 'var(--radius-md)'),
  borderWidth: size(fields.value.borderWidth, 'var(--border-width)'),
  padding: size(fields.value.padding, 'var(--space-16)'),
  checkboxSize: size(fields.value.checkboxSize, '1.5rem'),
  checkboxBg: color(fields.value.checkboxBgIdle, 'var(--color-background-subtle)'),
  checkboxBgSelected: color(
    fields.value.checkboxBgSelected,
    'var(--color-action-primary-background)',
  ),
  checkboxBorder: color(fields.value.checkboxBorderIdle, 'var(--color-action-tertiary-border)'),
  checkboxBorderSelected: color(
    fields.value.checkboxBorderSelected,
    'var(--color-action-primary-background)',
  ),
  checkboxFillSelected: color(
    fields.value.checkboxFillSelected,
    'var(--color-action-primary-text)',
  ),
}))

// Bound Value
const value = computed<string>({
  get() {
    return (pipe.getData(`var.${fields.value.valueVariable?.key ?? ''}`) ?? '') as string
  },
  set(val) {
    pipe.setData(`var.${fields.value.valueVariable?.key ?? ''}`, val)
  },
})

// Multi-select writes a separate string-array variable: the variable picker
// filters by one type, so `valueVariable` (a string) cannot hold the list.
const selectedValues = computed<string[]>({
  get() {
    const raw = pipe.getData(`var.${fields.value.multiValueVariable?.key ?? ''}`)
    return Array.isArray(raw) ? (raw as string[]) : []
  },
  set(val) {
    pipe.setData(`var.${fields.value.multiValueVariable?.key ?? ''}`, val)
  },
})

// Multi-select only: true while anything is picked, so a CTA's "Is Enabled
// Variable" can wait for a selection.
watch(
  () =>
    [
      fields.value.hasSelectionVariable?.key,
      isMultiSelect.value,
      selectedValues.value.length > 0,
    ] as const,
  ([key, isMulti, hasSelection]) => {
    if (!key || !isMulti) return
    pipe.setData(`var.${key}`, hasSelection)
  },
  { immediate: true },
)

const isSelected = (option: SelectListOption) =>
  isMultiSelect.value ? selectedValues.value.includes(option.value) : value.value === option.value

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handleSelect = (option: any) => {
  if (isMultiSelect.value) {
    selectedValues.value = nextSelectedValues(
      fields.value.options?.options ?? [],
      selectedValues.value,
      option,
    )
  } else {
    value.value = option.value
  }
  emit('onClick', option.value)
}

// Keyboard activation. Guarded to the item itself: the previous
// `@keydown.enter.prevent` / `@keydown.space.prevent` bindings fired for
// descendants too, so in the builder a space typed into the TipTap label was
// swallowed AND selected the option.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const onItemKeydown = (event: KeyboardEvent, option: any) => {
  if (event.target !== event.currentTarget) return
  if (event.key !== 'Enter' && event.key !== ' ') return
  event.preventDefault()
  handleSelect(option)
}
</script>

<style scoped lang="scss">
.select-list {
  display: grid;
  width: 100%;

  .list-item {
    height: v-bind(computedItemHeight);
  }
}

.select-list .list-item:not(.variant-image_card) {
  position: relative;
  cursor: pointer;
  background-color: v-bind('css.bg');
  border-width: v-bind('css.borderWidth');
  border-style: solid;
  border-color: v-bind('css.borderColor');
  border-radius: v-bind('css.borderRadius');
  overflow: hidden;
  transition: all 0.2s ease;

  // Keyboard focus ring stays unconditional (`:focus-visible` never matches a touch
  // tap by spec). Gate `:hover` behind a hover-capable pointer so touch devices, which
  // emulate `:hover` on tap and let it "stick", don't leave an item highlighted after a
  // tap-driven page navigation.
  &:focus-visible {
    outline: none;
    border-color: v-bind('css.borderColorHover');
    box-shadow: var(--shadow-selectors-default);
    transform: translateY(-2px);
  }

  @media (hover: hover) {
    &:hover {
      outline: none;
      border-color: v-bind('css.borderColorHover');
      box-shadow: var(--shadow-selectors-default);
      transform: translateY(-2px);
    }
  }

  &.active {
    border-color: v-bind('css.borderColorActive');
    background-color: v-bind('css.bgActive');
    box-shadow: var(--shadow-selectors-pressed);
  }
}

/* Base text styling */
.text-content {
  color: v-bind('css.textColor');
  font-family: var(--text-label-md-family);
  font-size: v-bind('css.textFontSize');
  line-height: v-bind('css.textLineHeight');
  font-weight: v-bind('css.textFontWeight');

  &.has-checkbox {
    display: flex;
    align-items: center;
    gap: var(--space-12);
    text-align: start;
  }
}

/* Checkbox (also rendered inside the image_card's ImageButton label row) */
.option-checkbox {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: v-bind('css.checkboxSize');
  height: v-bind('css.checkboxSize');
  border-width: var(--border-width);
  border-style: solid;
  border-radius: var(--radius-xs);
  background-color: v-bind('css.checkboxBg');
  border-color: v-bind('css.checkboxBorder');
  transition:
    background-color 0.2s,
    border-color 0.2s;

  svg {
    width: calc(v-bind('css.checkboxSize') * 5 / 6);
    height: calc(v-bind('css.checkboxSize') * 5 / 6);
    opacity: 0;
    transform: scale(0.5);
    transition: all 0.2s ease;
    stroke: v-bind('css.checkboxFillSelected');
    stroke-width: 3;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  &.checked {
    background-color: v-bind('css.checkboxBgSelected');
    border-color: v-bind('css.checkboxBorderSelected');

    svg {
      opacity: 1;
      transform: scale(1);
    }
  }
}

/* --------------------------------------
   VARIANT: TEXT
--------------------------------------- */
.variant-text {
  padding: v-bind('css.padding');
  text-align: center;
}

/* --------------------------------------
   VARIANT: IMAGE CARD (Stacked)
--------------------------------------- */
.variant-image_card {
  display: flex;
  flex-direction: column;
  text-align: center;

  .image-wrapper.stacked {
    width: 100%;
    aspect-ratio: 4 / 3;
    background-color: var(--color-background-subtle-accent);
    border-radius: var(--radius-sm);
    overflow: hidden;
    margin-bottom: var(--space-12);

    .card-img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.3s ease;
    }
  }

  @media (hover: hover) {
    &:hover .card-img {
      transform: scale(1.05);
    }
  }

  &.active :deep(.image-button) {
    border-color: v-bind('css.borderColorActive');
    background-color: v-bind('css.bgActive');
  }
}

/* --------------------------------------
   VARIANT: IMAGE INLINE (Left / Right)
--------------------------------------- */
.variant-image_inline_left,
.variant-image_inline_right {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  padding: 0;

  .text-content {
    flex: 1;
    display: flex;
    align-items: center;
    padding: v-bind('css.padding');
  }

  .image-wrapper.inline {
    display: flex;
    align-items: flex-end;
    justify-content: center;

    .inline-img {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
      object-position: bottom;
      display: block;
    }
  }
}

/* Utility for layout ordering */
.order-1 {
  order: 1;
}
.order-2 {
  order: 2;
}
</style>
